import React, { useState, useEffect, useRef } from 'react';
import { getBackend } from '../backend';
import Sheet from '../common/Sheet';

/**
 * ScaleWatcher enrollment (#86bc6dkfa, cloudapi ADR-025) — the POS tab's "ScaleWatcher" row for a
 * Dividia master on a Dragon cloud device. It says whether this device is in ScaleWatcher and, when
 * it is not, opens the "Add to ScaleWatcher" dialog that creates the device's Command Cloud site
 * under an existing or new ScaleWatcher customer.
 *
 * cloudapi derives everything that matters (serial = device record + 9999, the "<site> - <device>"
 * name, eligibility) and re-checks the master role on every call; this UI shows those values and
 * collects only the customer choice and customer type. Enrollment is idempotent server-side.
 */

const NEW_CUSTOMER = '__new__';
const TYPE_LABELS = {
  truck: 'Truck — scale + ticket',
  lpn: 'LPN — adds license plates',
  facility: 'Facility — adds in/out times',
};
const PROVIDER_LABELS = { commandcloud: 'Command Cloud' };
const CHECK_ICONS = { ok: '✓', warn: '!', fail: '✕' };

const providerLabel = (p) => PROVIDER_LABELS[p] || p || '';

// One credential line in the result view: label, the value (selectable), and a Copy button.
const CredRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(() => setCopied(true), () => {});
    }
  };
  return (
    <div className="setup-sw-sheet__cred">
      <span className="setup-sw-sheet__cred-label">{label}</span>
      <code className="setup-sw-sheet__cred-value">{value}</code>
      <button type="button" className="setup-btn--secondary setup-sw-sheet__copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
};

// The setup checklist cloudapi returns (the same ok/warn/fail rows the ScaleWatcher test console shows).
const Checklist = ({ checks }) => (
  <>
    <div className="setup-sw__title setup-sw-sheet__checks-title">Setup checklist</div>
    <ul className="setup-sw__checks">
      {checks.map((c) => (
        <li key={c.key} className={'setup-sw__check setup-sw__check--' + c.status}>
          <span className="setup-sw__check-icon" aria-hidden="true">{CHECK_ICONS[c.status] || '•'}</span>
          <span className="setup-sw__check-text">
            <span className="setup-sw__check-label">{c.label}</span>
            {c.detail ? <span className="setup-sw__check-detail">{c.detail}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  </>
);

export const ScalewatcherEnrollSheet = ({ deviceId, deviceName, token, data, onClose, onEnrolled }) => {
  const customers = data.customers || [];
  const types = data.customerTypes && data.customerTypes.length ? data.customerTypes : ['truck'];
  const dev = data.device || {};
  const [choice, setChoice] = useState('');   // '' | a customer id (string) | NEW_CUSTOMER
  const [newName, setNewName] = useState('');
  const [customerType, setCustomerType] = useState(data.defaultCustomerType || types[0]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [result, setResult] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const isNew = choice === NEW_CUSTOMER;
  const ready = !busy && (isNew ? newName.trim() !== '' : choice !== '');
  const ids = { customer: `sw-customer-${deviceId}`, name: `sw-new-customer-${deviceId}`, type: `sw-type-${deviceId}` };

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setFailure(null);
    const body = isNew
      ? { newCustomerName: newName.trim(), customerType }
      : { parentId: Number(choice), customerType };
    const res = await getBackend().enrollInScalewatcher(deviceId, body, { token });
    if (!alive.current) return;
    setBusy(false);
    if (res.ok) {
      setResult(res.result);
      if (onEnrolled) onEnrolled(res.result);
    } else {
      setFailure(res);
    }
  };

  // A new name that collides with an existing customer: offer that customer instead of retyping.
  const existingId = failure && failure.code === 'customer_exists' && failure.details && failure.details.customerId;
  const existing = existingId ? customers.find((c) => c.id === existingId) : null;
  const useExisting = () => { setChoice(String(existing.id)); setNewName(''); setFailure(null); };

  if (result) {
    const site = result.site || {};
    const customer = result.customer || null;
    const creds = customer && customer.created && customer.webhookSecret ? customer : null;
    const checks = (result.setupStatus && result.setupStatus.checks) || [];
    // The signing secret is shown once: while it is on screen, Esc, a click outside and the ✕ must not
    // close the sheet by accident (#86bc7maf2). With no onClose, Sheet offers none of them; Done remains.
    return (
      <Sheet className="app-sheet setup-sw-sheet" title={result.alreadyEnrolled ? 'Already in ScaleWatcher' : 'Added to ScaleWatcher'} onClose={creds ? undefined : onClose}>
        <div className="setup-sw-sheet__body">
          <p className="setup-sw-sheet__summary">
            {site.name} · serial {result.serial}{customer && customer.name ? ` · ${customer.name}` : ''}
          </p>
          {creds ? (
            <div className="setup-sw-sheet__creds">
              <div className="setup-sw__title">New customer’s Command Cloud credentials</div>
              <CredRow label="Customer ID" value={creds.webhookKey} />
              <CredRow label="Signing secret" value={creds.webhookSecret} />
              <span className="setup-hint">The signing secret is shown only once. Copy it now — an admin can re-mint it in ScaleWatcher.</span>
            </div>
          ) : null}
          {result.wTicketProviderWarning ? <div className="setup-notice">{result.wTicketProviderWarning}</div> : null}
          {checks.length ? <Checklist checks={checks} /> : null}
        </div>
        <div className="setup-sw-sheet__actions">
          <button type="button" className="setup-btn--save" onClick={onClose}>Done</button>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet className="app-sheet setup-sw-sheet" title="Add to ScaleWatcher" onClose={busy ? undefined : onClose}>
      <div className="setup-sw-sheet__body">
        <dl className="setup-sw-sheet__facts">
          <div className="setup-sw-sheet__fact"><dt>Device</dt><dd>{deviceName || dev.name}</dd></div>
          <div className="setup-sw-sheet__fact"><dt>ScaleWatcher site</dt><dd>{dev.siteName}</dd></div>
          <div className="setup-sw-sheet__fact"><dt>Serial</dt><dd>{dev.serial}</dd></div>
          <div className="setup-sw-sheet__fact"><dt>Ticket provider</dt><dd>{providerLabel(data.ticketProvider)}</dd></div>
        </dl>

        <div className="setup-sw-sheet__field">
          <label className="setup-sw-sheet__label" htmlFor={ids.customer}>Customer</label>
          <select id={ids.customer} className="setup-select" value={choice} disabled={busy}
            onChange={(e) => { setChoice(e.target.value); setFailure(null); }}>
            <option value="" disabled>Choose a customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}{c.hasWebhookCredentials ? '' : ' (no Command Cloud credentials)'}
              </option>
            ))}
            <option value={NEW_CUSTOMER}>+ New customer…</option>
          </select>
        </div>

        {isNew ? (
          <div className="setup-sw-sheet__field">
            <label className="setup-sw-sheet__label" htmlFor={ids.name}>New customer name</label>
            <input id={ids.name} className="setup-input" value={newName} maxLength={255} disabled={busy} autoFocus
              onChange={(e) => { setNewName(e.target.value); setFailure(null); }} />
            <span className="setup-hint">Creates a top-level ScaleWatcher customer with its own Command Cloud credentials.</span>
          </div>
        ) : null}

        <div className="setup-sw-sheet__field">
          <label className="setup-sw-sheet__label" htmlFor={ids.type}>Customer type</label>
          <select id={ids.type} className="setup-select" value={customerType} disabled={busy}
            onChange={(e) => setCustomerType(e.target.value)}>
            {types.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
          </select>
        </div>

        {failure ? (
          <div className="setup-error setup-sw-sheet__error" role="alert">
            <span>{failure.error}</span>
            {existing ? (
              <button type="button" className="setup-btn--secondary setup-sw-sheet__use" onClick={useExisting}>Use {existing.name}</button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="setup-sw-sheet__actions">
        <button type="button" className="setup-btn--secondary" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className="setup-btn--save" disabled={!ready} onClick={submit}>
          {busy ? 'Adding…' : 'Add to ScaleWatcher'}
        </button>
      </div>
    </Sheet>
  );
};

const ScalewatcherRow = ({ deviceId, deviceName, token }) => {
  const [loaded, setLoaded] = useState({ deviceId: null, data: null });
  const [reloadKey, setReloadKey] = useState(0);
  // The dialog's data, fixed while it is open: the status re-read after enrolling must not unmount
  // the sheet holding the one-time signing secret if that read fails (review L10).
  const [sheetData, setSheetData] = useState(null);
  // Cloud adapter only (the local NVR adapter has no ScaleWatcher enrollment): render nothing without it.
  const backend = getBackend();
  const supported = !!backend && typeof backend.getScalewatcherEnrollment === 'function';

  useEffect(() => {
    if (!supported) return undefined;
    let alive = true;
    getBackend().getScalewatcherEnrollment(deviceId, { token })
      .then((data) => { if (alive) setLoaded({ deviceId, data }); });
    return () => { alive = false; };
  }, [supported, deviceId, token, reloadKey]);

  if (!supported) return null;

  // Never show another device's status while this one loads.
  const data = loaded.deviceId === deviceId ? loaded.data : null;
  const device = data && data.ok ? data.device : null;
  const site = device && device.site;
  let status;
  let canAdd = false;
  if (!data) status = 'Checking ScaleWatcher…';
  else if (!data.ok) status = data.error || 'Could not check ScaleWatcher.';
  else if (site) status = `In ScaleWatcher as ${site.name} · serial ${site.serial}${site.parentName ? ` · ${site.parentName}` : ''}`;
  else if (device && !device.eligible) status = device.ineligibleReason || 'This device cannot be added to ScaleWatcher.';
  else { status = 'Not in ScaleWatcher yet.'; canAdd = true; }

  return (
    <div className="setup-sw">
      <div className="setup-sw__text">
        <span className="setup-sw__title">ScaleWatcher</span>
        <span className={'setup-sw__status' + (site ? ' setup-sw__status--on' : '')}>{status}</span>
      </div>
      {canAdd ? (
        <button type="button" className="setup-sw__add" onClick={() => setSheetData(data)}>Add to ScaleWatcher</button>
      ) : null}
      {sheetData ? (
        <ScalewatcherEnrollSheet deviceId={deviceId} deviceName={deviceName} token={token} data={sheetData}
          onClose={() => setSheetData(null)} onEnrolled={() => setReloadKey((k) => k + 1)} />
      ) : null}
    </div>
  );
};

export default ScalewatcherRow;
